import { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import translationsData from './translations.json';

export type Language = 'en' | 'bn';

type Translations = typeof translationsData.en;

interface TranslationContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (path: string) => string;
}

const TranslationContext = createContext<TranslationContextType | null>(null);

interface TranslationProviderProps {
  children: ReactNode;
  defaultLanguage?: Language;
}

/**
 * Get nested value from object using dot notation path
 */
function getByPath(obj: any, path: string): string {
  const parts = path.split('.');
  let current = obj;
  
  for (const part of parts) {
    if (current === undefined || current === null) return path;
    current = current[part];
  }
  
  return typeof current === 'string' ? current : path;
}

/**
 * Detect user's country using a free IP geolocation service
 * Returns ISO country code (e.g., 'BD' for Bangladesh, 'IN' for India)
 * 
 * Uses ipapi.co which provides free tier (1000 req/day) without API key
 */
async function detectCountry(): Promise<string | null> {
  try {
    const res = await fetch('https://ipapi.co/country/', { 
      cache: 'no-store',
      // Short timeout to avoid blocking page load
      signal: AbortSignal.timeout(3000)
    });
    if (!res.ok) return null;
    
    const countryCode = await res.text();
    // Validate it's a 2-letter country code
    return /^[A-Z]{2}$/.test(countryCode.trim()) ? countryCode.trim() : null;
  } catch {
    return null;
  }
}

/**
 * Check if user should see Bengali based on their region
 * - Bangladesh (BD): Always Bengali
 * - India (IN): Bengali if timezone suggests West Bengal (Asia/Kolkata with specific check)
 */
function shouldUseBengali(countryCode: string | null): boolean {
  if (!countryCode) return false;
  
  // Bangladesh - always Bengali
  if (countryCode === 'BD') return true;
  
  // India - check if likely West Bengal based on timezone
  // West Bengal uses Asia/Kolkata timezone, but so does most of India
  // We'll be generous and default to Bengali for all Indian users
  // since Bengali is widely understood in eastern India
  if (countryCode === 'IN') {
    // Additional heuristic: check browser language for Bengali preference
    const browserLang = navigator.language.toLowerCase();
    if (browserLang.startsWith('bn')) return true;
    
    // For now, don't auto-switch all Indian users to Bengali
    // Only if their browser indicates Bengali preference
    return false;
  }
  
  return false;
}

// Key to track if geo detection has been done
const GEO_DETECTED_KEY = 'enterpress-geo-detected';

export function TranslationProvider({ children, defaultLanguage = 'en' }: TranslationProviderProps) {
  // Check URL param or localStorage for initial language (synchronous)
  const getInitialLanguage = (): Language => {
    // 1. Check URL param first (highest priority - explicit user choice)
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      const langParam = urlParams.get('lang');
      if (langParam === 'bn' || langParam === 'en') {
        return langParam as Language;
      }
      
      // 2. Check localStorage (user's previous explicit choice)
      const stored = localStorage.getItem('enterpress-language');
      if (stored === 'bn' || stored === 'en') {
        return stored as Language;
      }
      
      // 3. Check browser language
      const browserLang = navigator.language.toLowerCase();
      if (browserLang.startsWith('bn')) {
        return 'bn';
      }
    }
    
    return defaultLanguage;
  };

  const [language, setLanguageState] = useState<Language>(getInitialLanguage);
  
  // Geo-based language detection (async, runs once on first visit)
  useEffect(() => {
    // Skip if user has already made an explicit choice or geo was already detected
    if (typeof window === 'undefined') return;
    
    const hasExplicitChoice = localStorage.getItem('enterpress-language');
    const geoAlreadyDetected = sessionStorage.getItem(GEO_DETECTED_KEY);
    const urlParams = new URLSearchParams(window.location.search);
    const hasUrlParam = urlParams.has('lang');
    
    if (hasExplicitChoice || geoAlreadyDetected || hasUrlParam) return;
    
    // Mark that we're doing geo detection (prevent duplicate calls)
    sessionStorage.setItem(GEO_DETECTED_KEY, 'true');
    
    detectCountry().then((countryCode) => {
      if (shouldUseBengali(countryCode)) {
        setLanguageState('bn');
      }
    });
  }, []);

  // Update HTML lang attribute, localStorage, and URL when language changes
  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.lang = language;
      localStorage.setItem('enterpress-language', language);
      
      const url = new URL(window.location.href);
      if (language === 'en') {
        url.searchParams.delete('lang');
      } else {
        url.searchParams.set('lang', language);
      }
      window.history.replaceState({}, '', url.toString());
    }
  }, [language]);

  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
  }, []);

  /**
   * Translate function using path (e.g., 'hero.headline')
   */
  const t = useCallback((path: string): string => {
    const langData = (translationsData as any)[language];
    if (!langData) return path;
    
    const translated = getByPath(langData, path);
    
    // Fallback to English if not found in current language
    if (translated === path && language !== 'en') {
      return getByPath(translationsData.en, path);
    }
    
    return translated;
  }, [language]);

  return (
    <TranslationContext.Provider value={{
      language,
      setLanguage,
      t,
    }}>
      {children}
    </TranslationContext.Provider>
  );
}

/**
 * Hook to access translation context
 */
export function useTranslation() {
  const context = useContext(TranslationContext);
  if (!context) {
    throw new Error('useTranslation must be used within a TranslationProvider');
  }
  return context;
}
